import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";

import "./quickbooks-entries-to-book.css";
import WComponent from "../../../../../common/WComponent";
import ToolsSelect from "../../../kit/ToolsSelect";
import ToolsModal from "../../../kit/ToolsModal";
import QuickBooksJournalModal from "./QuickBooksJournalModal";
import QuickBooksClientEntryModal from "./QuickBooksClientEntryModal";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import UtilityService from "../../../../../../services/UtilityService";

const NOTHING_OPEN = { kind: null, journal: null, reclassification: null, obeJournal: null, choices: {}, acknowledged: {}, targetQboAccountId: null, booking: false, bookedAs: null, error: "" };

class QuickBooksEntriesToBook extends WComponent {
	state = { entriesToBook: null, journals: [], reclassification: null, error: "", opened: NOTHING_OPEN, clientEntryFor: null, journalToRemove: null, removingJournalId: null, removeError: "", journalChangedInQuickBooks: null, journalStillMatchesTheEntry: null };

	componentDidMount = async () => {
		await this.load();
	};

	componentDidUpdate = async prevProps => {
		const changedEngagement = prevProps.wpfEngagementId !== this.props.wpfEngagementId;
		if (changedEngagement) await this.load();
	};

	load = async () => {
		const { clientId, wpfEngagementId } = this.props;
		if (!wpfEngagementId) return;
		const answer = await BookkeepingService.listEntriesToBookInQuickBooks({ clientId, wpfEngagementId });
		const error = answer.error || "";
		const entriesToBook = answer.error ? null : answer;
		await this.update({ entriesToBook, error });
		if (!entriesToBook || !entriesToBook.booksCameFromQuickBooks) return;

		const [booked, reclassification] = await Promise.all([
			BookkeepingService.listJournalsBookedInQuickBooks({ clientId, wpfEngagementId }),
			BookkeepingService.describeOpeningBalanceEquityReclassification({ clientId, wpfEngagementId }),
		]);
		const refusal = booked.error || reclassification.error || "";
		if (refusal) {
			await this.update({ error: refusal });
			return;
		}
		await this.update({ journals: booked.journals, reclassification: reclassification.reclassification || null });
	};

	onDownload = () => {
		const { entriesToBook } = this.state;
		const { headers, rows } = BookkeepingService.describeEntriesToBookAsCsvRows({ entriesToBook });
		const csvText = UtilityService.buildCsv({ headers, rows });
		const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
		UtilityService.downloadFile({ blob, fileName: `Entries to book in QuickBooks ${entriesToBook.periodEnd}.csv` });
	};

	onOpenAdjustingEntry = async entry => {
		const { clientId, wpfEngagementId } = this.props;
		const answer = await BookkeepingService.describeAdjustingEntryToBook({ clientId, wpfEngagementId, adjustingEntryHeaderId: entry.adjustingEntryHeaderId });
		if (answer.error) {
			UtilityService.alert({ type: "error", text: answer.error });
			return;
		}
		await this.update({ opened: { ...NOTHING_OPEN, kind: "adjusting_entry", journal: answer.journal } });
	};

	onOpenOpeningBalanceEquity = async obeJournal => {
		const { reclassification } = this.state;
		await this.update({ opened: { ...NOTHING_OPEN, kind: "opening_balance_equity", reclassification, obeJournal, targetQboAccountId: reclassification.recommendedQboAccountId } });
	};

	onClose = async () => {
		const wasBooked = !!this.state.opened.bookedAs;
		await this.update({ opened: NOTHING_OPEN });
		if (wasBooked) await this.afterQuickBooksChanged();
	};

	onOpenClientEntry = entry => this.update({ clientEntryFor: entry });

	onCloseClientEntry = async ({ recorded }) => {
		await this.update({ clientEntryFor: null });
		if (recorded) await this.afterQuickBooksChanged();
	};

	onChooseCandidate = ({ lineKey, value }) => {
		const { opened } = this.state;
		this.update({ opened: { ...opened, choices: { ...opened.choices, [lineKey]: value } } });
	};

	onChooseTarget = targetQboAccountId => {
		this.update({ opened: { ...this.state.opened, targetQboAccountId } });
	};

	onAcknowledgeWarning = ({ code, checked }) => {
		const { opened } = this.state;
		this.update({ opened: { ...opened, acknowledged: { ...opened.acknowledged, [code]: checked } } });
	};

	onBook = async () => {
		const { clientId, wpfEngagementId } = this.props;
		const { opened } = this.state;
		const acknowledgedWarnings = BookkeepingService.listAcknowledgedWarningCodes({ acknowledged: opened.acknowledged });
		await this.update({ opened: { ...opened, booking: true, error: "" } });
		let answer;
		if (opened.kind === "adjusting_entry") {
			const { chartAccountIdByLine } = BookkeepingService.describeAdjustingEntryForConfirmation({ journal: opened.journal, choices: opened.choices });
			answer = await BookkeepingService.bookAdjustingEntryInQuickBooks({ clientId, wpfEngagementId, adjustingEntryHeaderId: opened.journal.entry.id, chartAccountIdByLine, acknowledgedWarnings });
		} else {
			answer = await BookkeepingService.bookOpeningBalanceEquityReclassification({ clientId, wpfEngagementId, period: opened.obeJournal.period, targetQboAccountId: opened.targetQboAccountId, acknowledgedWarnings });
		}
		await this.update({ opened: { ...this.state.opened, booking: false, error: answer.error || "", bookedAs: answer.booked || null } });
	};

	onAskToRemove = journalToRemove => this.update({ journalToRemove, removeError: "", journalChangedInQuickBooks: null, journalStillMatchesTheEntry: null });

	onCancelRemove = () => this.update({ journalToRemove: null, removeError: "", journalChangedInQuickBooks: null, journalStillMatchesTheEntry: null });

	onRemove = async ({ removeEvenIfChanged, forgetEvenIfMatching } = {}) => {
		const { clientId, wpfEngagementId } = this.props;
		const { journalToRemove } = this.state;
		await this.update({ removingJournalId: journalToRemove.id, removeError: "" });
		const answer = await BookkeepingService.removeJournalFromQuickBooks({ clientId, wpfEngagementId, journalId: journalToRemove.id, removeEvenIfChanged, forgetEvenIfMatching });
		if (answer.error) {
			await this.update({ removingJournalId: null, removeError: answer.error, journalChangedInQuickBooks: answer.journalChangedInQuickBooks || null, journalStillMatchesTheEntry: answer.journalStillMatchesTheEntry || null });
			return;
		}
		await this.update({ removingJournalId: null, journalToRemove: null, journalChangedInQuickBooks: null, journalStillMatchesTheEntry: null });
		UtilityService.alert({ type: "success", text: BookkeepingService.describeJournalRemoval({ removal: answer.removal, qboJournalEntryId: journalToRemove.qboJournalEntryId }) });
		await this.afterQuickBooksChanged();
	};

	onRemoveEvenIfChanged = () => this.onRemove({ removeEvenIfChanged: true });

	onForgetEvenIfMatching = () => this.onRemove({ forgetEvenIfMatching: true });

	renderRemoveConfirmation = () => {
		const { journalToRemove, removingJournalId, removeError, journalChangedInQuickBooks, journalStillMatchesTheEntry } = this.state;
		if (!journalToRemove) return null;
		const removing = removingJournalId === journalToRemove.id;
		const isTheClientsJournal = journalToRemove.purpose === BookkeepingService.JOURNAL_PURPOSES.ENTERED_BY_CLIENT;
		const confirmLabel = isTheClientsJournal ? "It wasn't this entry" : "Remove from QuickBooks";
		const refusalIsAWarning = !!(journalChangedInQuickBooks || journalStillMatchesTheEntry);
		const footer = (
			<span className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={this.onCancelRemove} disabled={removing}>Keep it</button>
				{journalChangedInQuickBooks && (
					<button type="button" className="tk-button tk-button--danger" data-test="qbo-journal-remove-changed" onClick={this.onRemoveEvenIfChanged} disabled={removing}>
						{removing ? "Removing…" : "Delete the changed journal anyway"}
					</button>
				)}
				{journalStillMatchesTheEntry && (
					<button type="button" className="tk-button tk-button--danger" data-test="qbo-journal-forget-matching" onClick={this.onForgetEvenIfMatching} disabled={removing}>
						{removing ? "Working…" : "It is not this entry — forget the record"}
					</button>
				)}
				{!refusalIsAWarning && (
					<button type="button" className="tk-button tk-button--danger" data-test="qbo-journal-remove-confirm" onClick={() => this.onRemove({})} disabled={removing}>
						{removing ? "Working…" : confirmLabel}
					</button>
				)}
			</span>
		);
		const title = isTheClientsJournal ? `Stop treating journal ${journalToRemove.qboJournalEntryId} as this entry?` : `Remove journal ${journalToRemove.qboJournalEntryId} from QuickBooks?`;
		return (
			<ToolsModal open onClose={this.onCancelRemove} title={title} width={520} footer={footer}>
				{removeError && <div className={clsx("tk-notice", refusalIsAWarning ? "tk-notice--warning" : "tk-notice--danger")} data-test="qbo-journal-remove-refused"><p className="tk-notice__detail">{removeError}</p></div>}
				{isTheClientsJournal ? (
					<p className="tk-lead">
						Journal {journalToRemove.qboJournalEntryId} stays in QuickBooks — it is the client's. The adjusting entry goes back to being applied by
						the tool at once. Only do this if the journal is not this entry: if it is, the entry would be counted twice.
					</p>
				) : (
					<p className="tk-lead">
						This deletes the journal dated {UtilityService.formatDate({ value: journalToRemove.txnDate })} from the client's QuickBooks company. If the books saved here do not yet carry the
						journal, its adjusting entry is applied by the tool again at once; if they do, the entry stays retired until the next pull is saved, which
						applies it again — either way the statements end up the same.
					</p>
				)}
			</ToolsModal>
		);
	};

	afterQuickBooksChanged = async () => {
		await this.load();
		if (this.props.onQuickBooksChanged) await this.props.onQuickBooksChanged();
	};

	findBookedJournal = ({ adjustingEntryHeaderId }) => this.state.journals.find(journal => !journal.removedAt && String(journal.adjustingEntryHeaderId) === String(adjustingEntryHeaderId)) || null;

	renderLine = (line, index) => {
		const quickBooksName = BookkeepingService.describeChartAccount({ account: line.quickBooksAccount });
		const chartName = BookkeepingService.describeChartAccount({ account: line.accountLabel });
		return (
			<div key={index} className="tk-ledger__row qbbook__line">
				<span className={quickBooksName ? "qbbook__account" : "qbbook__account qbbook__account--missing"}>
					{quickBooksName || "Not linked to QuickBooks"}
					{chartName !== quickBooksName && <span className="qbbook__chart">{chartName}</span>}
				</span>
				<span className="tk-ledger__amount">{line.debit ? UtilityService.formatCurrency(line.debit) : ""}</span>
				<span className="tk-ledger__amount">{line.credit ? UtilityService.formatCurrency(line.credit) : ""}</span>
			</div>
		);
	};

	renderEntryAction = entry => {
		const { canPostJournalsToQuickBooks } = this.state.entriesToBook;
		const booked = this.findBookedJournal({ adjustingEntryHeaderId: entry.adjustingEntryHeaderId });
		if (booked) {
			const isTheClientsJournal = booked.purpose === BookkeepingService.JOURNAL_PURPOSES.ENTERED_BY_CLIENT;
			return (
				<span className="qbbook__booked" data-test="qbo-entry-booked">
					<span className="tk-status-dot tk-status-dot--success" />
					{BookkeepingService.describeBookedJournalState({ qboJournalEntryId: booked.qboJournalEntryId, entryStatus: booked.entryStatus, removedAt: booked.removedAt, purpose: booked.purpose })}
					{(isTheClientsJournal || canPostJournalsToQuickBooks) && (
						<button type="button" className="tk-text-action tk-text-action--muted tk-text-action--xs" data-test="qbo-journal-remove" onClick={() => this.onAskToRemove(booked)}>
							{isTheClientsJournal ? "This wasn't the client's entry" : "Remove from QuickBooks"}
						</button>
					)}
				</span>
			);
		}
		return (
			<span className="qbbook__actions">
				{canPostJournalsToQuickBooks && (
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" data-test="qbo-book-entry" onClick={() => this.onOpenAdjustingEntry(entry)}>
						Book in QuickBooks
					</button>
				)}
				<button type="button" className="tk-text-action tk-text-action--muted tk-text-action--xs" data-test="qbo-client-entered" onClick={() => this.onOpenClientEntry(entry)}>
					The client entered it
				</button>
			</span>
		);
	};

	renderEntry = entry => {
		return (
			<div key={entry.adjustingEntryHeaderId} className="qbbook__entry">
				<div className="qbbook__entry-head">
					<span className="qbbook__entry-number">Journal {entry.entryNumber}</span>
					<span className="qbbook__entry-memo">{entry.description}</span>
					<span className="qbbook__entry-action">{this.renderEntryAction(entry)}</span>
				</div>
				{entry.lines.map(this.renderLine)}
			</div>
		);
	};

	renderOpeningBalanceEquity = () => {
		const { reclassification } = this.state;
		if (!reclassification || reclassification.journals.length === 0) return null;
		return (
			<div className="qbbook__entry" data-test="qbo-obe-reclassification">
				<div className="qbbook__entry-head">
					<span className="qbbook__entry-number">Opening Balance Equity</span>
					<span className="qbbook__entry-memo">QuickBooks holds equity here that the statements do not print. Move it to the equity account it belongs to.</span>
				</div>
				{reclassification.journals.map(journal => (
					<div key={journal.period} className="tk-ledger__row qbbook__line">
						<span className="qbbook__account">
							{journal.period === "prior" ? `Balance at ${UtilityService.formatDate({ value: journal.txnDate })}` : `Moved during the year to ${UtilityService.formatDate({ value: journal.txnDate })}`}
							<span className="qbbook__chart">{UtilityService.formatCurrency(journal.amount)} {journal.balanceSide}</span>
						</span>
						<span />
						<span className="qbbook__entry-action">
							{journal.alreadyBooked ? (
								<span className="qbbook__booked"><span className="tk-status-dot tk-status-dot--success" /> Booked</span>
							) : this.state.entriesToBook.canPostJournalsToQuickBooks ? (
								<button type="button" className="tk-button tk-button--secondary tk-button--compact" data-test="qbo-book-obe" onClick={() => this.onOpenOpeningBalanceEquity(journal)} disabled={reclassification.refusals.length > 0}>
									Book in QuickBooks
								</button>
							) : null}
						</span>
					</div>
				))}
				{reclassification.refusals.length > 0 && (
					<div className="tk-notice tk-notice--warning qbbook__notice" data-test="qbo-obe-refusals"><p className="tk-notice__detail">{reclassification.refusals.join(" ")}</p></div>
				)}
			</div>
		);
	};

	renderTargetChoice = () => {
		const { opened } = this.state;
		const options = opened.reclassification.equityAccounts.map(account => ({ value: account.qboAccountId, label: BookkeepingService.describeChartAccount({ account }) }));
		const isTheRecommendation = opened.reclassification.recommendedBecause && opened.targetQboAccountId === opened.reclassification.recommendedQboAccountId;
		return (
			<div className="tk-form-field">
				<ToolsSelect value={opened.targetQboAccountId} options={options} onChange={this.onChooseTarget} placeholder="Choose the equity account to move it to" />
				{isTheRecommendation && <p className="tk-form-help">Recommended because {opened.reclassification.recommendedBecause}</p>}
			</div>
		);
	};

	renderModal = () => {
		const { opened } = this.state;
		if (!opened.kind) return null;
		if (opened.kind === "adjusting_entry") {
			const { journal } = opened;
			const confirmation = BookkeepingService.describeAdjustingEntryForConfirmation({ journal, choices: opened.choices });
			const everyWarningAcknowledged = BookkeepingService.everyWarningIsAcknowledged({ warnings: journal.warnings, acknowledged: opened.acknowledged });
			return (
				<QuickBooksJournalModal
					open
					onClose={this.onClose}
					title={`Book adjusting entry ${journal.entry.entryNumber} in QuickBooks`}
					company={journal.company.name}
					txnDate={journal.txnDate}
					memo={journal.memo}
					lines={confirmation.lines}
					totals={journal.totals}
					refusals={journal.refusals}
					warnings={journal.warnings}
					acknowledged={opened.acknowledged}
					onAcknowledgeWarning={this.onAcknowledgeWarning}
					error={opened.error}
					booking={opened.booking}
					bookedAs={opened.bookedAs}
					canBook={journal.canBook && confirmation.allChosen && everyWarningAcknowledged}
					onChooseCandidate={this.onChooseCandidate}
					onBook={this.onBook}
				/>
			);
		}
		const { reclassification, obeJournal } = opened;
		const confirmation = BookkeepingService.describeOpeningBalanceEquityJournalForConfirmation({ reclassification, journal: obeJournal, targetQboAccountId: opened.targetQboAccountId });
		const warningsForThisJournal = BookkeepingService.listWarningsForOpeningBalanceEquityJournal({ reclassification, journal: obeJournal });
		const everyWarningAcknowledged = BookkeepingService.everyWarningIsAcknowledged({ warnings: warningsForThisJournal, acknowledged: opened.acknowledged });
		return (
			<QuickBooksJournalModal
				open
				onClose={this.onClose}
				title="Move Opening Balance Equity in QuickBooks"
				company={reclassification.company.name}
				txnDate={obeJournal.txnDate}
				memo={`Move Opening Balance Equity as at ${obeJournal.txnDate} (booked from the year-end file)`}
				lines={confirmation.lines}
				totals={confirmation.totals}
				refusals={reclassification.refusals}
				warnings={warningsForThisJournal}
				acknowledged={opened.acknowledged}
				onAcknowledgeWarning={this.onAcknowledgeWarning}
				error={opened.error}
				booking={opened.booking}
				bookedAs={opened.bookedAs}
				canBook={reclassification.refusals.length === 0 && !!opened.targetQboAccountId && everyWarningAcknowledged}
				onBook={this.onBook}
			>
				{!opened.bookedAs && this.renderTargetChoice()}
			</QuickBooksJournalModal>
		);
	};

	render() {
		const { entriesToBook, error } = this.state;
		if (error) return <div className="tk-notice tk-notice--danger"><span className="tk-notice__detail">The entries to book in QuickBooks could not be loaded: {error}</span></div>;
		if (!entriesToBook || !entriesToBook.booksCameFromQuickBooks) return null;

		const hasEntries = entriesToBook.entries.length > 0;
		return (
			<div className="ye-package__section" data-test="qbo-entries-to-book">
				<div className="ye-package__section-title">
					Entries to book in QuickBooks
					<span className="ye-package__count">{UtilityService.describeCount({ count: entriesToBook.entries.length, singular: "journal" })}</span>
				</div>
				<p className="tk-lead">
					These books came from QuickBooks. Unless each adjusting entry ends up in QuickBooks dated {UtilityService.formatDate({ value: entriesToBook.periodEnd })} — booked from here or
					entered by the client — next year's QuickBooks opening balances will not match these signed statements.
				</p>
				{!entriesToBook.canPostJournalsToQuickBooks && (
					<div className="tk-notice tk-notice--warning qbbook__notice" data-test="qbo-posting-not-permitted">
						<div className="tk-notice__head">
							<span className="tk-notice__title"><Icon.Lock size={14} /> You cannot post or remove journals in QuickBooks</span>
						</div>
						<p className="tk-notice__detail">Ask an administrator to grant this under Manage Permissions. Accepted entries can still be marked as entered by the client.</p>
					</div>
				)}
				{entriesToBook.linesWithoutAQuickBooksAccount > 0 && (
					<div className="tk-notice tk-notice--warning qbbook__notice" data-test="qbo-lines-unlinked">
						<div className="tk-notice__head">
							<span className="tk-notice__title"><Icon.AlertTriangle size={14} /> {UtilityService.describeCount({ count: entriesToBook.linesWithoutAQuickBooksAccount, singular: "line" })} not linked to QuickBooks</span>
						</div>
						<p className="tk-notice__detail">Choose the QuickBooks account for those lines when booking.</p>
					</div>
				)}
				{this.renderOpeningBalanceEquity()}
				{hasEntries ? (
					<React.Fragment>
						{entriesToBook.entries.map(this.renderEntry)}
						<button type="button" className="tk-button tk-button--secondary qbbook__download" onClick={this.onDownload}>
							<Icon.Download size={14} /> Download for the client (CSV)
						</button>
					</React.Fragment>
				) : (
					<p className="qbbook__none"><Icon.Check size={14} /> No accepted adjusting entries — QuickBooks already matches these statements.</p>
				)}
				{this.renderModal()}
				{this.state.clientEntryFor && <QuickBooksClientEntryModal entry={this.state.clientEntryFor} clientId={this.props.clientId} wpfEngagementId={this.props.wpfEngagementId} onClose={this.onCloseClientEntry} />}
				{this.renderRemoveConfirmation()}
			</div>
		);
	}
}

export default QuickBooksEntriesToBook;
